import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../../utils/apiUtils';
import { safeAddToCart, monitorMemoryUsage, performMemoryCleanup, performLocalStorageCleanup, getObjectSize, emergencyMemoryCheck } from '../../../utils/storageUtils';
import { useCurrency } from '../../../contexts/CurrencyContext';

const PaymentStep = ({ formData, calculatePrice, isAddingToCart, addedToCart, handleAddToCart, handleGoToCart, mapGenerated, starMapRef, isGenerating }) => {
    const navigate = useNavigate();
    const { formatPrice } = useCurrency();

    // State for Create Package progress tracking
    const [isCreatingPackage, setIsCreatingPackage] = useState(false);
    const [packageProgress, setPackageProgress] = useState({ step: '', progress: 0 });
    const [packageAddedToCart, setPackageAddedToCart] = useState(false);

    // Debug function to safely log large objects
    const safeLog = (message, data) => {
        try {
            if (data && typeof data === 'object') {
                const size = getObjectSize(data);
                console.log(`${message} (Size: ${(size / 1024).toFixed(2)}KB)`, data);
            } else {
                console.log(message, data);
            }
        } catch (error) {
            console.warn('Error logging data:', error);
            console.log(message, '[Large object - logging skipped]');
        }
    };

    // Enhanced cart addition with debugging and memory management
    const safeAddToCartWithDebug = async (cartItem) => {
        try {
            console.log('🛒 Starting cart addition process...');

            // 1. Memory check before starting
            const memoryBefore = monitorMemoryUsage();
            safeLog('📊 Memory status before cart addition:', memoryBefore);

            // 2. Clean up memory if pressure is high
            if (!memoryBefore.available) {
                console.log('🧹 Performing memory cleanup...');
                performMemoryCleanup();
                await new Promise(resolve => setTimeout(resolve, 100)); // Brief pause
            }

            // 3. Create a minimal cart item to reduce memory footprint
            const minimalCartItem = {
                id: cartItem.id,
                name: cartItem.name,
                price: cartItem.price,
                quantity: cartItem.quantity,
                imageUrl: cartItem.imageUrl, // Server-hosted URL (small)
                // Essential display data only
                date: cartItem.date,
                location: cartItem.location,
                caption: cartItem.caption,
                size: cartItem.size,
                frame: cartItem.frame,
                printType: cartItem.printType,
                // Mark that files are ready on server
                serverFilesReady: cartItem.serverFilesReady || true,
                // Reference to server package (if exists)
                packageId: cartItem.packageId
            };

            safeLog('🔍 Minimal cart item created:', minimalCartItem);

            // 4. Check size before adding
            const itemSize = getObjectSize(minimalCartItem);
            console.log(`📦 Cart item size: ${(itemSize / 1024).toFixed(2)}KB`);

            if (itemSize > 50 * 1024) { // If larger than 50KB, it might be too big
                console.warn('⚠️ Cart item is very large, further reducing...');

                // Ultra-minimal version
                const ultraMinimalItem = {
                    id: cartItem.id,
                    name: cartItem.name,
                    price: cartItem.price,
                    quantity: cartItem.quantity,
                    imageUrl: cartItem.imageUrl,
                    date: cartItem.date,
                    location: cartItem.location,
                    caption: cartItem.caption,
                    serverFilesReady: true
                };

                return await safeAddToCart(ultraMinimalItem);
            }

            // 5. Add to cart with retries
            let attempts = 0;
            const maxAttempts = 3;

            while (attempts < maxAttempts) {
                attempts++;
                console.log(`🎯 Cart addition attempt ${attempts}/${maxAttempts}`);

                try {
                    const success = await safeAddToCart(minimalCartItem);

                    if (success) {
                        console.log('✅ Cart addition successful');

                        // Memory check after success
                        const memoryAfter = monitorMemoryUsage();
                        safeLog('📊 Memory status after cart addition:', memoryAfter);

                        return true;
                    } else {
                        console.warn(`⚠️ Cart addition failed on attempt ${attempts}`);

                        if (attempts < maxAttempts) {
                            // Clean up and try again
                            performMemoryCleanup();
                            await new Promise(resolve => setTimeout(resolve, 200));
                        }
                    }
                } catch (error) {
                    console.error(`❌ Cart addition error on attempt ${attempts}:`, error);

                    if (attempts < maxAttempts) {
                        performMemoryCleanup();
                        await new Promise(resolve => setTimeout(resolve, 300));
                    } else {
                        throw error;
                    }
                }
            }

            return false;

        } catch (error) {
            console.error('❌ Critical error in cart addition:', error);

            // Emergency memory cleanup
            try {
                performMemoryCleanup();
            } catch (cleanupError) {
                console.error('Failed to cleanup memory:', cleanupError);
            }

            return false;
        }
    };

    // Calculate base price depending on print type and size (matching CreateMapPage.js logic)
    const getBasePrice = () => {
        const digitalSizePrices = {
            '21cm x 30cm': 10.00,
            '30cm x 40cm': 15.00,
            '60cm x 90cm': 20.00
        };

        const printedSizePrices = {
            '21cm x 30cm': 50.00,
            '30cm x 40cm': 75.00,
            '60cm x 90cm': 100.00
        };

        if (formData.printType === 'digital') {
            return digitalSizePrices[formData.size] || 10.00;
        } else {
            return printedSizePrices[formData.size] || 50.00;
        }
    };

    // Frame pricing for printed maps only (matching CreateMapPage.js logic)
    const getFramePrice = () => {
        if (formData.printType === 'digital') return 0;

        if (formData.frame === 'Classic Black' || formData.frame === 'Classic White') {
            return 50.00;
        } else if (formData.frame === 'Silver') {
            return 60.00;
        }
        return 0;
    };

    // Background pricing (matching CreateMapPage.js logic)
    const getBackgroundPrice = () => {
        const backgroundPrices = {
            'basic-light': 5.00,
            'basic-dark': 5.00,
            'sunset-pink': 8.00,
            'sunset-orange': 8.00,
            'pastel-pink': 6.50,
            'pastel-blue': 6.50,
            'pastel-white': 6.50,
            'neon-purple': 9.00,
            'neon-blue': 9.00,
            'natural-gold': 7.00,
            'natural-brown': 7.00,
            'natural-beige': 7.00,
            'golden-dark': 11.00,
            'golden-black': 11.00,
            'golden-gold': 11.00
        };

        return formData.background ? backgroundPrices[formData.background] || 0 : 0;
    };

    // Map style pricing (matching CreateMapPage.js logic)
    const getMapStylePrice = () => {
        const mapStylePrices = {
            'classic': 0.00,
            'minimal': 0.00,
            'vintage': 15.00,
            'cosmic': 15.00,
            'modern': 15.00,
            'nebula': 25.00,
            'midnight': 25.00,
            'constellation': 25.00
        };

        return formData.mapStyle ? mapStylePrices[formData.mapStyle] || 0 : 0;
    };

    // Orbital style pricing (matching CreateMapPage.js logic)
    const getOrbitalStylePrice = () => {
        const orbitalStylePrices = {
            'classic': 0.00,
            'minimal': 0.00,
            'geometric': 2.50,
            'celestial': 3.50,
            'cosmic': 4.00,
            'spiral': 3.00,
            'wave': 3.50,
            'orbit': 4.50,
            'heart': 5.50,
            'starburst': 5.50,
            'asymmetric': 5.00,
            'star': 6.00,
            'galaxy': 6.50
        };

        return formData.orbitalStyle ? orbitalStylePrices[formData.orbitalStyle] || 0 : 0;
    };

    // Get background name for display
    const getBackgroundName = () => {
        const types = {
            'basic-light': 'Basic Harmony (Light)',
            'basic-dark': 'Basic Harmony (Dark)',
            'sunset-pink': 'Sunset Vibe (Pink)',
            'sunset-orange': 'Sunset Vibe (Orange)',
            'pastel-pink': 'Pastel Whispers (Pink)',
            'pastel-blue': 'Pastel Whispers (Blue)',
            'pastel-white': 'Pastel Whispers (White)',
            'neon-purple': 'Neon Pop (Purple)',
            'neon-blue': 'Neon Pop (Blue)',
            'natural-gold': 'Natural Soul (Gold)',
            'natural-brown': 'Natural Soul (Brown)',
            'natural-beige': 'Natural Soul (Beige)',
            'golden-dark': 'Golden Elegance (Dark)',
            'golden-black': 'Golden Elegance (Black)',
            'golden-gold': 'Golden Elegance (Gold)'
        };

        return formData.background ? types[formData.background] : 'None';
    };

    // Celestial element helper (matching CreateMapPage.js logic)
    const getCelestialElements = () => {
        const elements = [
            { id: 'showStars', name: 'Stars', price: 4.00 },
            { id: 'showConstellations', name: 'Constellation lines', price: 5.00 },
            { id: 'showConstellationNames', name: 'Constellation names', price: 5.00 },
            { id: 'showGrid', name: 'Coordination Grid', price: 8.00 },
            { id: 'showDeepSkyObjects', name: 'Deep Sky Objects', price: 13.00 },
            { id: 'showEcliptic', name: 'Ecliptic line', price: 17.50 }
        ];

        return elements.filter(element => formData[element.id]);
    };

    // NEW: Capture SVG function that duplicates Extract SVG button functionality AND saves to server
    const handleCaptureSVG = async () => {
        console.log('🧪 Testing complete preview SVG extraction with server save...');

        // First check if the star map is ready
        if (!mapGenerated || isGenerating) {
            console.warn('⚠️ Star map is not ready yet. Please wait for generation to complete.');
            alert('Please wait for the star map to finish generating before testing SVG extraction.');
            return;
        }

        if (starMapRef.current && starMapRef.current.extractCompleteMapPreview) {
            try {
                console.log('⏳ Starting complete preview extraction (this may take a few seconds)...');
                console.log('🎨 This will capture the entire preview including orbital decorations, background, and text.');

                // Give the page time to fully render all elements
                await new Promise(resolve => setTimeout(resolve, 2000));

                const extractedSVG = await starMapRef.current.extractCompleteMapPreview(formData.background);

                if (extractedSVG) {
                    console.log('✅ Complete preview extraction successful!');

                    console.log('📊 Complete SVG details:', {
                        width: extractedSVG.getAttribute('width'),
                        height: extractedSVG.getAttribute('height'),
                        viewBox: extractedSVG.getAttribute('viewBox'),
                        children: extractedSVG.children.length,
                        hasBackground: extractedSVG.querySelector('rect') ? 'Yes' : 'No',
                        hasStarMap: extractedSVG.querySelector('g') ? 'Yes' : 'No',
                        hasText: extractedSVG.querySelector('text') ? 'Yes' : 'No',
                        hasDecorations: extractedSVG.querySelector('image') ? 'Yes' : 'No'
                    });

                    // Get SVG as string for download AND server save
                    const svgString = starMapRef.current.getSVGString(extractedSVG);
                    if (svgString) {
                        console.log('📝 Complete SVG string length:', svgString.length, 'characters');

                        // 1. LOCAL DOWNLOAD (original functionality)
                        const blob = new Blob([svgString], { type: 'image/svg+xml' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'complete-starmap-preview.svg';
                        a.style.display = 'none';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);

                        console.log('💾 Complete preview SVG downloaded successfully');

                        // 2. SERVER SAVE (new functionality)
                        try {
                            console.log('🚀 Saving SVG to server...');

                            // Convert to base64 data URI (same as Add to Cart)
                            const svgDataUri = 'data:image/svg+xml;base64,' + btoa(svgString);

                            // Generate temporary ID for this capture
                            const tempId = 'capture-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

                            // Save to server with same structure as Add to Cart
                            const saveResponse = await fetch(buildApiUrl('save-starmap'), {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    temp_order_id: tempId,
                                    svgData: svgDataUri,
                                    captureMethod: 'capture-svg-button',
                                    // Include all form data for server processing
                                    date: formData.date,
                                    location: formData.location,
                                    locationName: formData.locationName,
                                    caption: formData.caption,
                                    size: formData.size,
                                    frame: formData.frame,
                                    printType: formData.printType,
                                    background: formData.background || 'golden-black',
                                    mapStyle: formData.mapStyle,
                                    orbitalStyle: formData.orbitalStyle,
                                    showStars: formData.showStars,
                                    showConstellations: formData.showConstellations,
                                    showConstellationNames: formData.showConstellationNames,
                                    showGrid: formData.showGrid,
                                    showDeepSkyObjects: formData.showDeepSkyObjects,
                                    showEcliptic: formData.showEcliptic
                                })
                            });

                            const saveResult = await saveResponse.json();
                            if (saveResult.success) {
                                console.log('✅ SVG saved to server successfully');
                                console.log('📁 Server file info:', saveResult);

                                // Enhanced success message
                                alert('✅ Complete preview SVG captured successfully!\n\n🎨 Includes: orbital decorations, background, star map, and text\n💾 Downloaded locally to your computer\n🚀 Also saved to server for processing\n📁 Check your downloads folder for complete-starmap-preview.svg');
                            } else {
                                console.warn('⚠️ Failed to save SVG to server:', saveResult.message);

                                // Partial success message
                                alert('✅ Complete preview SVG extracted successfully!\n\n🎨 Includes: orbital decorations, background, star map, and text\n💾 Downloaded locally to your computer\n⚠️ Server save failed - but local download succeeded\n📁 Check your downloads folder for complete-starmap-preview.svg');
                            }
                        } catch (serverError) {
                            console.error('❌ Server save error:', serverError);

                            // Partial success message
                            alert('✅ Complete preview SVG extracted successfully!\n\n🎨 Includes: orbital decorations, background, star map, and text\n💾 Downloaded locally to your computer\n⚠️ Server save failed - but local download succeeded\n📁 Check your downloads folder for complete-starmap-preview.svg');
                        }
                    } else {
                        throw new Error('Failed to serialize complete SVG to string');
                    }
                } else {
                    console.error('❌ Complete preview extraction failed');
                    alert('Complete preview extraction failed. Check the console for details.');
                }
            } catch (error) {
                console.error('❌ Complete preview extraction error:', error);
                alert(`Complete preview extraction failed: ${error.message}`);
            }
        } else {
            console.error('❌ Complete preview extraction not available');
            alert('Complete preview extraction function is not available. Please ensure the star map is loaded.');
        }
    };

    // NEW: Capture PDF function that duplicates Generate PDF button functionality AND saves to server
    const handleCapturePDF = async () => {
        console.log('🧪 Testing jsPDF PDF generation with server save...');

        // First check if the star map is ready
        if (!mapGenerated || isGenerating) {
            console.warn('⚠️ Star map is not ready yet. Please wait for generation to complete.');
            alert('Please wait for the star map to finish generating before testing PDF generation.');
            return;
        }

        if (starMapRef.current && starMapRef.current.generatePDFWithJsPDF) {
            try {
                console.log('⏳ Starting jsPDF generation (this may take a few seconds)...');
                console.log('📄 This will generate a high-quality PDF using SVG extraction and jsPDF.');

                // Give the page time to fully render all elements
                await new Promise(resolve => setTimeout(resolve, 2000));

                const pdfBlob = await starMapRef.current.generatePDFWithJsPDF(formData.background);

                if (pdfBlob) {
                    console.log('✅ jsPDF generation successful!');
                    console.log(`📦 PDF size: ${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB`);

                    // 1. LOCAL DOWNLOAD (original functionality)
                    const url = URL.createObjectURL(pdfBlob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'starmap-jspdf-test.pdf';
                    a.style.display = 'none';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);

                    console.log('💾 jsPDF generated and downloaded successfully');

                    // 2. SERVER SAVE (new functionality)
                    try {
                        console.log('🚀 Saving PDF to server...');

                        // Convert PDF blob to base64 data URI
                        const pdfDataUri = await new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => resolve(reader.result);
                            reader.onerror = reject;
                            reader.readAsDataURL(pdfBlob);
                        });

                        // Generate temporary ID for this capture
                        const tempId = 'pdf-capture-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

                        // Save to server with same structure as SVG capture
                        const saveResponse = await fetch(buildApiUrl('save-pdf'), {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                temp_order_id: tempId,
                                pdfData: pdfDataUri,
                                captureMethod: 'capture-pdf-button',
                                // Include all form data for server processing
                                date: formData.date,
                                location: formData.location,
                                locationName: formData.locationName,
                                caption: formData.caption,
                                size: formData.size,
                                frame: formData.frame,
                                printType: formData.printType,
                                background: formData.background || 'golden-black',
                                mapStyle: formData.mapStyle,
                                orbitalStyle: formData.orbitalStyle,
                                showStars: formData.showStars,
                                showConstellations: formData.showConstellations,
                                showConstellationNames: formData.showConstellationNames,
                                showGrid: formData.showGrid,
                                showDeepSkyObjects: formData.showDeepSkyObjects,
                                showEcliptic: formData.showEcliptic
                            })
                        });

                        const saveResult = await saveResponse.json();
                        if (saveResult.success) {
                            console.log('✅ PDF saved to server successfully');
                            console.log('📁 Server file info:', saveResult);

                            // Enhanced success message
                            alert(`✅ jsPDF PDF captured successfully!\n\n📄 High-quality PDF generated using SVG extraction\n💾 Downloaded locally to your computer\n🚀 Also saved to server for processing\n📦 Size: ${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB\n📁 Check your downloads folder for starmap-jspdf-test.pdf`);
                        } else {
                            console.warn('⚠️ Failed to save PDF to server:', saveResult.message);

                            // Partial success message
                            alert(`✅ jsPDF PDF generated successfully!\n\n📄 High-quality PDF generated using SVG extraction\n💾 Downloaded locally to your computer\n⚠️ Server save failed - but local download succeeded\n📦 Size: ${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB\n📁 Check your downloads folder for starmap-jspdf-test.pdf`);
                        }
                    } catch (serverError) {
                        console.error('❌ Server save error:', serverError);

                        // Partial success message
                        alert(`✅ jsPDF PDF generated successfully!\n\n📄 High-quality PDF generated using SVG extraction\n💾 Downloaded locally to your computer\n⚠️ Server save failed - but local download succeeded\n📦 Size: ${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB\n📁 Check your downloads folder for starmap-jspdf-test.pdf`);
                    }
                } else {
                    console.error('❌ jsPDF generation failed');
                    alert('jsPDF generation failed. Check the console for details.');
                }
            } catch (error) {
                console.error('❌ jsPDF generation error:', error);
                alert(`jsPDF generation failed: ${error.message}`);
            }
        } else {
            console.error('❌ jsPDF generation not available');
            alert('jsPDF generation function is not available. Please ensure the star map is loaded.');
        }
    };

    // NEW: Handle going to cart after package creation
    const handleGoToCartFromPackage = () => {
        navigate('/cart');
    };

    // NEW: Create package function that combines SVG and PDF generation and uploads to server (no local download)
    const handleCreatePackage = async () => {
        console.log('🎯 Creating package - combining SVG and PDF generation with server upload...');

        // Reset package added to cart state when starting new package creation
        setPackageAddedToCart(false);

        // First check if the star map is ready
        if (!mapGenerated || isGenerating) {
            console.warn('⚠️ Star map is not ready yet. Please wait for generation to complete.');
            alert('Please wait for the star map to finish generating before creating package.');
            return;
        }

        if (starMapRef.current && starMapRef.current.extractCompleteMapPreview && starMapRef.current.generatePDFWithJsPDF) {
            try {
                // Start progress tracking
                setIsCreatingPackage(true);
                setPackageProgress({ step: 'Initializing...', progress: 10 });

                console.log('⏳ Starting package creation (this may take a few seconds)...');
                console.log('📦 This will generate both SVG and PDF files and upload them to the server.');

                // Give the page time to fully render all elements
                await new Promise(resolve => setTimeout(resolve, 2000));

                // Generate temporary ID for this package
                const tempId = 'package-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

                // 1. Extract SVG
                setPackageProgress({ step: 'Extracting SVG...', progress: 25 });
                console.log('🎨 Extracting SVG...');
                const extractedSVG = await starMapRef.current.extractCompleteMapPreview(formData.background);

                if (!extractedSVG) {
                    throw new Error('Failed to extract SVG');
                }

                const svgString = starMapRef.current.getSVGString(extractedSVG);
                if (!svgString) {
                    throw new Error('Failed to serialize SVG to string');
                }

                console.log('✅ SVG extraction successful');

                // 2. Generate PDF
                setPackageProgress({ step: 'Generating PDF...', progress: 50 });
                console.log('📄 Generating PDF...');
                const pdfBlob = await starMapRef.current.generatePDFWithJsPDF(formData.background);

                if (!pdfBlob) {
                    throw new Error('Failed to generate PDF');
                }

                console.log('✅ PDF generation successful');
                console.log(`📦 PDF size: ${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB`);

                // 3. Upload both files to server
                setPackageProgress({ step: 'Uploading to server...', progress: 75 });
                console.log('🚀 Uploading package to server...');

                // Convert SVG to base64 data URI
                const svgDataUri = 'data:image/svg+xml;base64,' + btoa(svgString);

                // Convert PDF blob to base64 data URI
                const pdfDataUri = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(pdfBlob);
                });

                // Upload both files in parallel
                const [svgResult, pdfResult] = await Promise.all([
                    // Upload SVG
                    fetch(buildApiUrl('save-starmap'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            temp_order_id: tempId + '-svg',
                            svgData: svgDataUri,
                            captureMethod: 'create-package-svg',
                            // Include all form data for server processing
                            date: formData.date,
                            location: formData.location,
                            locationName: formData.locationName,
                            caption: formData.caption,
                            size: formData.size,
                            frame: formData.frame,
                            printType: formData.printType,
                            background: formData.background || 'golden-black',
                            mapStyle: formData.mapStyle,
                            orbitalStyle: formData.orbitalStyle,
                            showStars: formData.showStars,
                            showConstellations: formData.showConstellations,
                            showConstellationNames: formData.showConstellationNames,
                            showGrid: formData.showGrid,
                            showDeepSkyObjects: formData.showDeepSkyObjects,
                            showEcliptic: formData.showEcliptic
                        })
                    }).then(response => response.json()),

                    // Upload PDF
                    fetch(buildApiUrl('save-pdf'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            temp_order_id: tempId + '-pdf',
                            pdfData: pdfDataUri,
                            captureMethod: 'create-package-pdf',
                            // Include all form data for server processing
                            date: formData.date,
                            location: formData.location,
                            locationName: formData.locationName,
                            caption: formData.caption,
                            size: formData.size,
                            frame: formData.frame,
                            printType: formData.printType,
                            background: formData.background || 'golden-black',
                            mapStyle: formData.mapStyle,
                            orbitalStyle: formData.orbitalStyle,
                            showStars: formData.showStars,
                            showConstellations: formData.showConstellations,
                            showConstellationNames: formData.showConstellationNames,
                            showGrid: formData.showGrid,
                            showDeepSkyObjects: formData.showDeepSkyObjects,
                            showEcliptic: formData.showEcliptic
                        })
                    }).then(response => response.json())
                ]);

                // Check results
                setPackageProgress({ step: 'Finalizing...', progress: 95 });
                const svgSuccess = svgResult.success;
                const pdfSuccess = pdfResult.success;

                if (svgSuccess && pdfSuccess) {
                    console.log('✅ Package uploaded successfully to server');
                    console.log('📁 SVG file info:', svgResult);
                    console.log('📁 PDF file info:', pdfResult);

                    setPackageProgress({ step: 'Adding to cart...', progress: 98 });

                    // Create cart item using server-hosted SVG
                    const mapId = tempId; // Use the same ID as the package
                    const price = calculatePrice();

                    // Get SVG URL from server response - construct the URL based on the response
                    let svgImageUrl = '/images/products/default-map.jpg'; // fallback
                    if (svgResult.svg_url) {
                        svgImageUrl = svgResult.svg_url;
                    } else if (svgResult.file_path) {
                        // Construct URL from file path
                        svgImageUrl = `/api/downloads/${svgResult.file_path.split('/').pop()}`;
                    }

                    // Create ULTRA-LIGHTWEIGHT cart item for package (files already on server)
                    const cartItem = {
                        id: mapId,
                        name: formData.caption || 'Custom Star Map',
                        price: price,
                        quantity: 1,
                        imageUrl: svgImageUrl, // Server-hosted SVG (small URL)
                        // MINIMAL display data only
                        date: formData.date,
                        location: formData.location,
                        locationName: formData.locationName, // Store the city name for display
                        caption: formData.caption,
                        size: formData.size,
                        frame: formData.frame,
                        printType: formData.printType,
                        // Mark as server-processed (no heavy config needed)
                        serverFilesReady: true,
                        packageId: tempId // Reference to server package
                    };

                    console.log('🛒 Adding item to cart with server SVG:', cartItem);

                    // Add to cart with enhanced error handling
                    try {
                        console.log('🛒 Attempting to add package to cart...');

                        // Pre-emptive cleanup to maximize available space
                        console.log('🧹 Performing pre-cart cleanup...');
                        performMemoryCleanup();

                        // Clean up localStorage bloat
                        const freedSpace = await performLocalStorageCleanup();
                        if (freedSpace > 0) {
                            console.log(`🧹 Freed ${(freedSpace / (1024 * 1024)).toFixed(2)}MB of localStorage space`);
                        }

                        // Brief pause to let cleanup complete
                        await new Promise(resolve => setTimeout(resolve, 100));

                        // Quick localStorage status check
                        const currentCart = localStorage.getItem('cart');
                        if (currentCart) {
                            const cartSizeKB = ((currentCart.length * 2) / 1024).toFixed(1);
                            console.log(`🛒 Current cart size: ${cartSizeKB}KB`);
                        }

                        // Final memory check before cart addition
                        const finalMemoryCheck = await emergencyMemoryCheck();
                        if (!finalMemoryCheck) {
                            throw new Error('Insufficient memory for cart addition - Please refresh page and try again');
                        }

                        const cartSuccess = await safeAddToCartWithDebug(cartItem);

                        if (cartSuccess) {
                            // Notify other components of cart update
                            window.dispatchEvent(new Event('cartUpdated'));

                            setPackageProgress({ step: 'Complete!', progress: 100 });

                            // Wait a moment to show completion
                            await new Promise(resolve => setTimeout(resolve, 1000));

                            // Set package added to cart state
                            setPackageAddedToCart(true);

                            // Removed confirmation alert for cleaner UX
                            console.log(`✅ Package created and added to cart! SVG file uploaded to server, PDF file uploaded to server, Item added to your cart, PDF size: ${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB, Package ID: ${tempId}`);
                        } else {
                            console.warn('⚠️ Failed to add item to cart');

                            setPackageProgress({ step: 'Complete!', progress: 100 });

                            // Wait a moment to show completion
                            await new Promise(resolve => setTimeout(resolve, 1000));

                            alert(`✅ Package created successfully!\n\n🎨 SVG file uploaded to server\n📄 PDF file uploaded to server\n⚠️ Could not add to cart (storage full?)\n📦 PDF size: ${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB\n📁 Package ID: ${tempId}`);
                        }
                    } catch (cartError) {
                        console.error('❌ Error adding to cart:', cartError);

                        setPackageProgress({ step: 'Complete!', progress: 100 });

                        // Wait a moment to show completion
                        await new Promise(resolve => setTimeout(resolve, 1000));

                        alert(`✅ Package created successfully!\n\n🎨 SVG file uploaded to server\n📄 PDF file uploaded to server\n❌ Error adding to cart: ${cartError.message}\n📦 PDF size: ${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB\n📁 Package ID: ${tempId}`);
                    }
                } else {
                    console.warn('⚠️ Package upload partially failed');
                    console.warn('SVG result:', svgResult);
                    console.warn('PDF result:', pdfResult);

                    const successCount = (svgSuccess ? 1 : 0) + (pdfSuccess ? 1 : 0);
                    const failureDetails = [];
                    if (!svgSuccess) failureDetails.push('SVG upload failed');
                    if (!pdfSuccess) failureDetails.push('PDF upload failed');

                    alert(`⚠️ Package creation partially successful\n\n✅ ${successCount}/2 files uploaded successfully\n❌ ${failureDetails.join(', ')}\n📦 Package ID: ${tempId}`);
                }

            } catch (error) {
                console.error('❌ Package creation error:', error);
                alert(`❌ Package creation failed: ${error.message}`);
            } finally {
                // Reset progress state
                setIsCreatingPackage(false);
                setPackageProgress({ step: '', progress: 0 });
            }
        } else {
            console.error('❌ Package creation functions not available');
            alert('Package creation functions are not available. Please ensure the star map is loaded.');
        }
    };

    return (
        <div className="space-y-6">

            <div className="-mt-4">
                <h3 className="text-[31px] font-light mb-4">Review Your Order</h3>
                <p className="mb-8 text-gray-300 text-md">
                    If you'd like to make any changes, please go back to the previous tabs.
                </p>
            </div>

            <div className="border-t border-dotted border-[#A0A0A0] mt-10 mb-8"></div>

            <div className="bg-black/30 rounded-lg !text-md">
                <div className="space-y-2 mb-4">
                    <div className="flex justify-between">
                        <span className="text-white">Star Map</span>
                        <span>{formatPrice(getBasePrice())}</span>
                    </div>

                    {formData.background && getBackgroundPrice() > 0 && (
                        <div className="flex justify-between">
                            <span className="text-white">Background ({getBackgroundName()})</span>
                            <span>+{formatPrice(getBackgroundPrice())}</span>
                        </div>
                    )}

                    {formData.mapStyle && getMapStylePrice() > 0 && (
                        <div className="flex justify-between">
                            <span className="text-white">Map Style ({formData.mapStyle})</span>
                            <span>+{formatPrice(getMapStylePrice())}</span>
                        </div>
                    )}

                    {formData.orbitalStyle && getOrbitalStylePrice() > 0 && (
                        <div className="flex justify-between">
                            <span className="text-white">Orbital Style ({formData.orbitalStyle})</span>
                            <span>+{formatPrice(getOrbitalStylePrice())}</span>
                        </div>
                    )}

                    {formData.printType !== 'digital' && formData.frame && formData.frame !== 'none' && getFramePrice() > 0 && (
                        <div className="flex justify-between">
                            <span className="text-white">Frame ({formData.frame})</span>
                            <span>+{formatPrice(getFramePrice())}</span>
                        </div>
                    )}

                    {getCelestialElements().map(element => (
                        <div key={element.id} className="flex justify-between">
                            <span className="text-white">{element.name}</span>
                            <span>+{formatPrice(element.price)}</span>
                        </div>
                    ))}

                    <div className="border-t border-dotted border-[#A0A0A0] !mt-6 !mb-6"></div>

                    <div className="flex justify-between font-bold">
                        <span>Total</span>
                        <span className="text-accent">{formatPrice(calculatePrice())}</span>
                    </div>

                    <div className="border-t border-dotted border-[#A0A0A0] !mt-6 !mb-6"></div>

                </div>

                <div className="text-md text-gray-400 mb-3">
                    <p className="mb-2 font-semibold">For printed maps:</p>
                    <p className="mb-2">• Flat-fee {formatPrice(20)} shipping (added on checkout)</p>
                    <p className="mb-2">• Printed on premium archival paper</p>
                    <p className="mb-2">• Ships within 15 business days</p>
                </div>

                <div className="border-t border-dotted border-[#A0A0A0] !mt-6 !mb-6"></div>

            </div>

            <div className="bg-black/30 rounded-lg !mt-8 p-4">
                <div className="mb-4">
                    <p className="text-sm text-gray-300">
                        You'll receive a download link for your star map PDF after completing payment. The download link will be emailed to you along with your order confirmation.
                    </p>
                </div>

                {/* Add to Cart button - REMOVED FOR PRODUCTION
                <div className="flex flex-col md:flex-row gap-4">
                    <button
                        onClick={addedToCart ? handleGoToCart : handleAddToCart}
                        disabled={!mapGenerated || isAddingToCart}
                        className={`w-full md:w-[300px] mx-auto flex items-center justify-center py-3 px-6 rounded-[50px] font-semibold transition duration-300 ${!mapGenerated || isAddingToCart ? 'bg-gray-600 cursor-not-allowed' : 'bg-accent text-primary hover:bg-accent/90'}`}
                    >
                        {isAddingToCart ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Generating the map</span>
                            </>
                        ) : addedToCart ? (
                            <>
                                <span className="!text-[14px] md:!text-md !text-white">Go to cart →</span>
                            </>
                        ) : (
                            <>
                                <span className="!text-[14px] md:!text-md !text-white">Add to Cart</span>
                            </>
                        )}
                    </button>
                </div>
                */}

                {/* NEW: Capture SVG Button - HIDDEN FOR PRODUCTION
                <div className="flex flex-col md:flex-row gap-4 mt-3">
                    <button
                        onClick={handleCaptureSVG}
                        disabled={!mapGenerated || isGenerating}
                        className={`w-full md:w-[300px] mx-auto flex items-center justify-center py-3 px-6 rounded-[50px] font-semibold transition duration-300 ${!mapGenerated || isGenerating ? 'bg-gray-600 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                        {isGenerating ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Map generating...</span>
                            </>
                        ) : (
                            <>
                                <span className="!text-[14px] md:!text-md !text-white">Capture SVG</span>
                            </>
                        )}
                    </button>
                </div>

                NEW: Capture PDF Button - HIDDEN FOR PRODUCTION
                <div className="flex flex-col md:flex-row gap-4 mt-3">
                    <button
                        onClick={handleCapturePDF}
                        disabled={!mapGenerated || isGenerating}
                        className={`w-full md:w-[300px] mx-auto flex items-center justify-center py-3 px-6 rounded-[50px] font-semibold transition duration-300 ${!mapGenerated || isGenerating ? 'bg-gray-600 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
                    >
                        {isGenerating ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Map generating...</span>
                            </>
                        ) : (
                            <>
                                <span className="!text-[14px] md:!text-md !text-white">Capture PDF</span>
                            </>
                        )}
                    </button>
                </div>
                */}

                {/* Create map and add to Cart Button */}
                <div className="flex flex-col md:flex-row gap-4 mt-3">
                    <div className="w-full md:w-[300px] mx-auto">
                        <button
                            onClick={packageAddedToCart ? handleGoToCartFromPackage : handleCreatePackage}
                            disabled={!mapGenerated || isGenerating || (isCreatingPackage && !packageAddedToCart)}
                            className={`w-full flex items-center justify-center py-3 px-6 rounded-[50px] font-semibold transition duration-300 ${!mapGenerated || isGenerating || (isCreatingPackage && !packageAddedToCart)
                                ? 'bg-gray-600 cursor-not-allowed'
                                : packageAddedToCart
                                    ? 'bg-accent text-primary hover:bg-accent/90'
                                    : 'bg-accent text-primary hover:bg-accent/90'
                                }`}
                        >
                            {isGenerating ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Map generating...</span>
                                </>
                            ) : isCreatingPackage ? (
                                <>
                                    <span className="!text-[14px] md:!text-md !text-white">Creating your star map</span>
                                </>
                            ) : packageAddedToCart ? (
                                <>
                                    <span className="!text-[14px] md:!text-md !text-white">Go to cart →</span>
                                </>
                            ) : (
                                <>
                                    <span className="!text-[14px] md:!text-md !text-white">Generate and add to Cart</span>
                                </>
                            )}
                        </button>

                        {/* Progress Bar */}
                        {isCreatingPackage && (
                            <div className="mt-3 w-full">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm text-gray-300">{packageProgress.step}</span>
                                    <span className="text-sm text-gray-300">{packageProgress.progress}%</span>
                                </div>
                                <div className="w-full bg-gray-700 rounded-full h-2">
                                    <div
                                        className="bg-purple-600 h-2 rounded-full transition-all duration-300 ease-out"
                                        style={{ width: `${packageProgress.progress}%` }}
                                    ></div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentStep; 